/**
 * duplicateEvidence — pure functions for detecting duplicate expenses
 * before submission. Extracted from useReceiptAnalysis to make duplicate
 * detection testable without rendering React.
 *
 * Responsibilities:
 * - Call the duplicate check API with receipt AI extraction results
 * - Return structured duplicate warnings for UI display
 */

import { authService } from '@/lib/authService';
import type { ReceiptAiPayload } from '@/lib/receiptAiAnalysis';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('duplicateEvidence');

export interface DuplicateMatch {
  id: string;
  item_number?: string | null;
  title: string;
  status: string;
  grand_total: number;
  expense_date?: string | null;
  receipt_number?: string | null;
  match_reason: string;
  match_confidence: string;
  created_at: string;
}

export interface DuplicateEvidenceResult {
  duplicates: DuplicateMatch[] | null;
  checking: boolean;
}

/**
 * Check for duplicate expenses using the server-side duplicate detection API.
 * Called after AI receipt extraction populates vendor, receipt number, amount,
 * and date fields.
 *
 * Returns null if no duplicates found, or an array of duplicate matches.
 */
export async function checkDuplicateEvidence(
  receiptResult: ReceiptAiPayload | null,
  contactId?: number | null,
  overrides?: { expenseDate?: string | null; receiptNumber?: string | null },
): Promise<DuplicateMatch[] | null> {
  if (!receiptResult) return null;

  const vendor = receiptResult.vendor_name || undefined;
  const receiptNumber = overrides?.receiptNumber || receiptResult.receipt_number || undefined;
  const amount = receiptResult.total_amount != null ? Number(receiptResult.total_amount) : undefined;
  // Prefer the user-confirmed form datetime — it includes the slip time the
  // user was forced to pick, which is the strongest disambiguation signal.
  const expenseDate = overrides?.expenseDate || receiptResult.receipt_date || undefined;

  // If we have a contact_id, we can check even without vendor name
  if (!vendor && !contactId) {
    return null;
  }

  // Need at least one additional signal beyond vendor/contact to meaningfully check
  if (!receiptNumber && !amount && !expenseDate) {
    return null;
  }

  const token = authService.getTokens()?.access_token;
  if (!token) return null;

  try {
    const res = await fetch('/api/v1/expenses/check-duplicate', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vendor_name: vendor,
        contact_id: contactId || undefined,
        receipt_number: receiptNumber || undefined,
        amount: amount || undefined,
        expense_date: expenseDate || undefined,
      }),
    });

    if (!res.ok) return null;

    const data = await res.json();
    if (data.has_duplicates && Array.isArray(data.duplicates)) {
      return data.duplicates as DuplicateMatch[];
    }

    return null;
  } catch (err) {
    if (!isSilentFetchError(err)) logger.error('[duplicateEvidence] Duplicate check error:', err);
    return null;
  }
}
